import {
  AUTO_START_CHOSEN,
  AUTO_START_DEVICE,
  AUTO_START_NEW,
  AUTO_START_PARAM,
  VIA_BOOKMARK,
  VIA_PARAM,
} from "@/contracts/session";
import type { SessionState } from "@/convex/session";
import {
  type DeviceReader,
  deviceReaders,
  isDeviceReader,
  lastReader,
} from "@/storage/device-readers";
import {
  landedBookmark,
  markDeviceUnconfirmed,
  markUnconfirmed,
  noteBookmarkLanding,
  takeBookmarkLanding,
} from "@/storage/unconfirmed-reader";
import type { ReaderStart } from "./enter";
import { readerEntry } from "./links";

const AUTH_ERROR_PARAM = "auth_error";

/**
 * What a reader-entry round trip tried: the device's reader, a fresh one,
 * or a reader picked by name from the device's list.
 */
export type AutoStartKind =
  | typeof AUTO_START_DEVICE
  | typeof AUTO_START_NEW
  | typeof AUTO_START_CHOSEN;
/** What signed the reader in on a landing: an attempt, or a scanned bookmark. */
export type EntryLanding = AutoStartKind | typeof VIA_BOOKMARK;

const AUTO_START_KINDS: readonly string[] = [
  AUTO_START_DEVICE,
  AUTO_START_NEW,
  AUTO_START_CHOSEN,
];

function startAs(
  location: { pathname: string; search: string },
  readerId: string | null,
  kind: AutoStartKind,
): ReaderStart {
  const params = new URLSearchParams(location.search);
  // A bookmark landing's via would gate the reader this entry signs in, and
  // an earlier failure must not decline this landing.
  params.delete(VIA_PARAM);
  params.delete(AUTH_ERROR_PARAM);
  params.set(AUTO_START_PARAM, kind);
  const landing = boundedLanding(
    `${location.pathname}?${params.toString()}`,
    location.pathname,
    kind,
  );
  return { entry: readerEntry(readerId, landing), landing };
}

/**
 * A round trip through the reader entry back to this page, whose landing
 * says what was tried: the device's reader if it has one, else a fresh one.
 */
export function readerStart(
  location: { pathname: string; search: string },
  deviceReaderId: string | null = lastReader(),
): ReaderStart {
  return startAs(
    location,
    deviceReaderId,
    deviceReaderId === null ? AUTO_START_NEW : AUTO_START_DEVICE,
  );
}

/**
 * The round trip for a reader picked by name from the device's list. Its
 * landing is marked, so a failure declines like any attempt, but as a
 * choice, which nothing gates: the person has just said who they are.
 */
export function chosenReaderStart(
  location: { pathname: string; search: string },
  readerId: string,
): ReaderStart {
  return startAs(location, readerId, AUTO_START_CHOSEN);
}

// The landing is already URL-encoded ASCII, so even form-encoded three
// bytes a character it keeps the POST under the Worker's 4 KiB body cap. A
// page whose query is longer returns without it, still marked as the attempt.
export const MAX_LANDING_LENGTH = 1_024;
const FALLBACK_PATH = "/reader";

function boundedLanding(
  landing: string,
  pathname: string,
  kind: AutoStartKind,
): string {
  if (landing.length <= MAX_LANDING_LENGTH) return landing;
  const marker = `?${AUTO_START_PARAM}=${kind}`;
  const path =
    pathname.length + marker.length <= MAX_LANDING_LENGTH
      ? pathname
      : FALLBACK_PATH;
  return `${path}${marker}`;
}

/** Whether the page is an attempt's landing, failed or not. */
function landedAttempt(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has(AUTO_START_PARAM) || params.has(AUTH_ERROR_PARAM);
}

/**
 * Where a signed-out visitor to a reader route goes to get a reader without
 * an account. Null on the landing of an attempt, failed or not, so a
 * session that never sticks renders the route's own signed-out view
 * instead of looping through the broker.
 */
export function autoStart(
  location: { pathname: string; search: string },
  deviceReaderId: string | null = lastReader(),
): ReaderStart | null {
  if (landedAttempt(location.search)) return null;
  return readerStart(location, deviceReaderId);
}

/**
 * What a signed-out reader route does: render itself on an attempt's
 * landing, start the one reader the device remembers (or a fresh one), or
 * ask which of several remembered readers this is rather than guess the
 * most recent. The list is most recent first, as the device keeps it.
 */
export type SignedOutEntry =
  | { kind: "declined" }
  | { kind: "start"; start: ReaderStart }
  | { kind: "choose"; readers: DeviceReader[] };

export function signedOutEntry(
  location: { pathname: string; search: string },
  readers: DeviceReader[] = deviceReaders(),
): SignedOutEntry {
  if (landedAttempt(location.search)) return { kind: "declined" };
  if (readers.length >= 2) return { kind: "choose", readers };
  return {
    kind: "start",
    start: readerStart(location, readers[0]?.readerId ?? null),
  };
}

function isAutoStartKind(kind: string | null): kind is AutoStartKind {
  return kind !== null && AUTO_START_KINDS.includes(kind);
}

export function landedEntry(search: string): EntryLanding | null {
  if (landedBookmark(search)) return VIA_BOOKMARK;
  const kind = new URLSearchParams(search).get(AUTO_START_PARAM);
  return isAutoStartKind(kind) ? kind : null;
}

/**
 * A reader signed in by identification alone comes back unconfirmed: a
 * bookmark scan signs in whoever holds the link, and on a shared device an
 * account sign-out must not hand the next person the last reader's library
 * unasked. The landing is read from the URL once, before the route strips
 * it, and spent by the first session answer, so an answer that arrives
 * after in-app navigation, or after a first read that failed or said
 * signed out, still gates, while a re-read of the same session cannot gate
 * a reader who has already said "That's me". The landing never names a
 * reader: the session answer does, so a replayed landing can at worst gate
 * the current reader again, and a marker only ever adds to the others. A
 * device attempt gates only a reader the device remembers: a fresh one the
 * Worker minted instead lands ungated, and so does an account. A reader
 * picked by name lands ungated too.
 *
 * A bookmark landing is also recorded on the device the moment it is read,
 * and the next reader_session answer in any tab spends the record: the
 * landing tab's read may fail or lag while another tab, already open on
 * the reader the scan signed in, would otherwise show it ungated. The
 * record names no reader and lapses after {@link BOOKMARK_LANDING_TTL_MS}.
 */
export function settleEntry(
  landing: EntryLanding | null,
  {
    deviceReader = isDeviceReader,
    now = Date.now,
  }: { deviceReader?: (readerId: string) => boolean; now?: () => number } = {},
): (state: SessionState) => void {
  const recorded = landing === VIA_BOOKMARK && noteBookmarkLanding(now());
  let pending = landing;
  return (state) => {
    if (state.status !== "ready") return;
    const kind = pending;
    pending = null;
    const { session } = state;
    if (session.lane !== "reader_session") return;
    const unrecordedLanding = kind === VIA_BOOKMARK && !recorded;
    if (takeBookmarkLanding(now()) || unrecordedLanding) {
      markUnconfirmed(session.readerId, now());
    } else if (kind === AUTO_START_DEVICE && deviceReader(session.readerId)) {
      markDeviceUnconfirmed(session.readerId, now());
    }
  };
}
